import { useState, useEffect, useRef } from 'react';
import { Bell, X, Check, CheckCheck, Trash2, UserCheck, UserX } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import { playNotificationDing } from '../../lib/notificationSound';

interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  category: 'job' | 'assignment' | 'message' | 'system' | 'community' | 'document' | 'contract' | 'timesheet' | 'talent' | 'billing' | 'admin_message' | 'interview_request';
  read: boolean;
  link: string | null;
  created_at: string;
  metadata: any;
  related_id: string | null;
}

interface NotificationBellProps {
  variant?: 'light' | 'dark';
}

export default function NotificationBell({ variant = 'light' }: NotificationBellProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [connectionActionLoading, setConnectionActionLoading] = useState<string | null>(null);
  const prevUnreadCountRef = useRef<number | null>(null);

  useEffect(() => {
    if (user) {
      fetchNotifications();

      const channel = supabase
        .channel('notifications-changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${user.id}`
          },
          () => {
            fetchNotifications();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [user]);

  const fetchNotifications = async () => {
    if (!user) return;

    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(20);

    if (!error && data) {
      const newUnread = data.filter(n => !n.read).length;
      if (prevUnreadCountRef.current !== null && newUnread > prevUnreadCountRef.current) {
        playNotificationDing();
      }
      prevUnreadCountRef.current = newUnread;
      setNotifications(data);
      setUnreadCount(newUnread);
    }
  };

  const markAsRead = async (notificationId: string) => {
    await supabase.rpc('mark_notification_read', { notification_id: notificationId });
    fetchNotifications();
  };

  const markAllAsRead = async () => {
    setLoading(true);
    await supabase.rpc('mark_all_notifications_read');
    await fetchNotifications();
    setLoading(false);
  };

  const deleteNotification = async (notificationId: string) => {
    await supabase
      .from('notifications')
      .delete()
      .eq('id', notificationId);
    fetchNotifications();
  };

  const isConnectionRequest = (notification: Notification) => {
    return (
      notification.title === 'New Connection Request' &&
      notification.category === 'community' &&
      notification.metadata?.connection_id
    );
  };

  const handleAcceptConnection = async (notification: Notification) => {
    const connectionId = notification.metadata?.connection_id;
    if (!connectionId) return;

    setConnectionActionLoading(notification.id);
    try {
      await supabase
        .from('community_connections')
        .update({ status: 'accepted', updated_at: new Date().toISOString() })
        .eq('id', connectionId);

      await supabase
        .from('notifications')
        .delete()
        .eq('id', notification.id);

      fetchNotifications();
    } finally {
      setConnectionActionLoading(null);
    }
  };

  const handleDenyConnection = async (notification: Notification) => {
    const connectionId = notification.metadata?.connection_id;
    if (!connectionId) return;

    setConnectionActionLoading(notification.id);
    try {
      await supabase
        .from('community_connections')
        .update({ status: 'declined', updated_at: new Date().toISOString() })
        .eq('id', connectionId);

      await supabase
        .from('notifications')
        .delete()
        .eq('id', notification.id);

      fetchNotifications();
    } finally {
      setConnectionActionLoading(null);
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'success': return 'bg-green-100 text-green-800 border-green-200';
      case 'warning': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'error': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-blue-100 text-blue-800 border-blue-200';
    }
  };

  const getCategoryIcon = (category: string) => {
    const icons: Record<string, string> = {
      job: '💼',
      assignment: '📋',
      message: '💬',
      system: '⚙️',
      community: '👥',
      document: '📄',
      contract: '📝',
      timesheet: '⏰',
      talent: '⭐',
      billing: '💳',
      admin_message: '📨',
      interview_request: '🤝'
    };
    return icons[category] || '📢';
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`relative p-2 rounded-lg transition-colors ${
          variant === 'dark'
            ? 'text-white hover:bg-white/10'
            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
        }`}
      >
        <Bell className="w-6 h-6" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-[600px] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
              <h3 className="font-semibold text-gray-900">Notifications</h3>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    disabled={loading}
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1 disabled:opacity-50"
                  >
                    <CheckCheck className="w-4 h-4" />
                    Mark all read
                  </button>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="overflow-y-auto flex-1">
              {notifications.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Bell className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>No notifications yet</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`p-4 hover:bg-gray-50 transition-colors ${
                        !notification.read ? 'bg-blue-50' : ''
                      }`}
                    >
                      <div className="flex gap-3">
                        <div className="flex-shrink-0 text-2xl">
                          {getCategoryIcon(notification.category)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <h4 className="font-medium text-gray-900 text-sm">
                              {notification.title}
                            </h4>
                            <span className="text-xs text-gray-500 whitespace-nowrap">
                              {formatTime(notification.created_at)}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">
                            {notification.message}
                          </p>

                          {isConnectionRequest(notification) ? (
                            <div className="flex items-center gap-2 mt-2">
                              <button
                                onClick={() => handleAcceptConnection(notification)}
                                disabled={connectionActionLoading === notification.id}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-md transition-colors disabled:opacity-50"
                              >
                                <UserCheck className="w-3.5 h-3.5" />
                                Accept
                              </button>
                              <button
                                onClick={() => handleDenyConnection(notification)}
                                disabled={connectionActionLoading === notification.id}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs font-medium rounded-md transition-colors disabled:opacity-50"
                              >
                                <UserX className="w-3.5 h-3.5" />
                                Decline
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <span className={`text-xs px-2 py-1 rounded-full border ${getTypeColor(notification.type)}`}>
                                {notification.type}
                              </span>
                              {(notification.link || (notification.type === 'group_chat_invite' && notification.related_id)) && (
                                <button
                                  className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                                  onClick={() => {
                                    setIsOpen(false);
                                    if ((notification.type === 'group_chat_invite' || notification.type === 'group_chat_added') && notification.related_id) {
                                      navigate(`/consultant/community?group=${notification.related_id}`);
                                    } else {
                                      navigate(notification.link!);
                                    }
                                  }}
                                >
                                  View details →
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex-shrink-0 flex items-start gap-1">
                          {!notification.read && !isConnectionRequest(notification) && (
                            <button
                              onClick={() => markAsRead(notification.id)}
                              className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded transition-colors"
                              title="Mark as read"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                          )}
                          {!isConnectionRequest(notification) && (
                            <button
                              onClick={() => deleteNotification(notification.id)}
                              className="p-1 text-red-600 hover:text-red-800 hover:bg-red-100 rounded transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
